import type { AxiosError } from 'axios';
import {
  Box,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type {
  Activity,
  ActivityAssignee,
  ActivityHour,
  ActivityLine,
  Contract,
  ContractLine,
  Space,
  WorkPackage
} from '@/api/generated/models';
import type { CardTab, InfoField } from '@/components/common';
import type { ActivityPickedItem } from '@/features/activities/components/ActivityItemPickerModal';
import type { DraftSummary, DraftValue } from '@/utils/useItemsDraft';
import {
  getGetActivityQueryKey,
  getListActivitiesQueryKey,
  getListActivityAssigneesQueryKey,
  getListActivityHoursQueryKey,
  getListActivityLinesQueryKey,
  useDeleteActivity,
  useGetActivity,
  useListActivityAssignees,
  useListActivityHours,
  useListActivityLines,
  useRemoveActivityAssignee,
  useSaveActivityHours,
  useSaveActivityLines
} from '@/api/generated/activities/activities';
import {
  getListContractLinesQueryKey,
  useGetContract,
  useListContractLines
} from '@/api/generated/contracts/contracts';
import { ContractContractType } from '@/api/generated/models/contractContractType';
import { useListUsers } from '@/api/generated/users/users';
import {
  useGetSpace,
  useGetWorkPackage,
  useListPlannedItems,
  useListSpaces
} from '@/api/generated/work-structure/work-structure';
import { IconFigmaXCircle } from '@/assets/icons';
import {
  BackButton,
  CardSection,
  CardTabs,
  ConfirmActionModal,
  DRAFT_ROW_BACKGROUND,
  DraftNumberInput,
  InfoCard,
  ItemsSaveBar,
  ItemsSaveStatus,
  ListPagination,
  ListTotalCard,
  MobileActionBar,
  MobileCard,
  MobileCardList,
  MobileCodeBadge,
  NewItemBadge,
  StatusBanner,
  StatusPill,
  StatusTracker,
  tableRowStyle,
  TotalAmountCell,
  UndoRemoveButton,
  useUnsavedChangesGuard
} from '@/components/common';
import {
  activityLockState,
  buildActivityTrackerSteps
} from '@/features/activities/activityDisplay';
import { ActivityAssigneePickerModal } from '@/features/activities/components/ActivityAssigneePickerModal';
import { ActivityItemPickerModal } from '@/features/activities/components/ActivityItemPickerModal';
import { ActivityWorkerPickerModal } from '@/features/activities/components/ActivityWorkerPickerModal';
import { InfoBulletList, PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import { formatWeekLabel } from '@/features/week-reports/weekReportDisplay';
import { buildSpaceBreadcrumb } from '@/features/work-packages/workPackageDisplay';
import { formatAmount } from '@/utils/format';
import { useItemsDraft } from '@/utils/useItemsDraft';
import { useLocalPagination } from '@/utils/useLocalPagination';
import { useSavedMessage } from '@/utils/useSavedMessage';

const activityErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

const RemoveRowButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={t('activityDetail.colRemove')}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
      style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
    >
      <IconFigmaXCircle size={16} />
    </Box>
  );
};

/** Redak stavke: spremljeni redak uz nespremljene promjene ili nova stavka iz nacrta. */
interface DisplayLine {
  key: string;
  lineId?: string;
  tempId?: string;
  plannedItemId?: string;
  /** Stavka ugovora — izravno ili preko planirane stavke radnog paketa. */
  contractLineId?: string;
  itemCode?: string;
  description?: string;
  unit?: string;
  isNew: boolean;
  isRemoved: boolean;
  realizedQty: DraftValue;
  savedQty: number;
  isChanged: boolean;
  error: string | null;
}

/** Redak sati po radniku — isto kao DisplayLine. */
interface DisplayHour {
  key: string;
  hourId?: string;
  tempId?: string;
  userId: string;
  isNew: boolean;
  isRemoved: boolean;
  hours: DraftValue;
  savedHours: number;
  isChanged: boolean;
  error: string | null;
}

const hasDraftState = (row: {
  isNew: boolean;
  isRemoved: boolean;
  isChanged: boolean;
  error: string | null;
}) => row.isNew || row.isRemoved || row.isChanged || !!row.error;

const rowActionWidth = 60;

interface ActivityLineRowProps {
  row: DisplayLine;
  index: number;
  disabled: boolean;
  contractLine?: ContractLine;
  plannedQty?: number;
  onChange: (value: DraftValue) => void;
  onRemove: () => void;
  variant?: 'row' | 'card';
}

const ActivityLineRow = ({
  row,
  index,
  disabled,
  contractLine,
  plannedQty,
  onChange,
  onRemove,
  variant = 'row'
}: ActivityLineRowProps) => {
  const { t } = useTranslation();

  const recordedQty = contractLine?.recordedQty ?? 0;
  const recordedValue = recordedQty * (contractLine?.unitPrice ?? 0);
  const remainingQty =
    contractLine != null ? (contractLine.quantity ?? 0) - recordedQty : undefined;
  const remainingValue =
    contractLine != null ? (contractLine.totalPrice ?? 0) - recordedValue : undefined;

  // Ostvareni iznos retka = ostvarena količina × jedinična cijena stavke ugovora (isti izračun kao
  // Iznos na listi aktivnosti); prati i nespremljenu vrijednost polja.
  const achievedAmount = row.isRemoved
    ? 0
    : (Number(row.realizedQty) || 0) * (contractLine?.unitPrice ?? 0);
  const mutedText = row.isRemoved ? 'var(--color-text-muted)' : undefined;

  const qtyField = disabled ? (
    <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
      {row.savedQty}
    </Text>
  ) : (
    <DraftNumberInput
      width={90}
      height={variant === 'card' ? 40 : 32}
      value={row.realizedQty}
      savedValue={row.savedQty}
      isNew={row.isNew}
      error={row.error}
      disabled={row.isRemoved}
      ariaLabel={t('activityDetail.colRealizedQty')}
      onChange={onChange}
    />
  );

  const action = row.isRemoved ? (
    <UndoRemoveButton onClick={onRemove} />
  ) : (
    <RemoveRowButton onClick={onRemove} />
  );

  const description = (
    <Text
      fz={variant === 'card' ? 13.5 : 13.5}
      fw={variant === 'card' ? 600 : undefined}
      style={{
        color:
          mutedText ??
          (variant === 'card' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'),
        textDecoration: row.isRemoved ? 'line-through' : undefined,
        minWidth: 0
      }}
    >
      {row.description}
    </Text>
  );

  if (variant === 'card') {
    const isOver = remainingQty != null && remainingQty < 0;
    return (
      <MobileCard background={!disabled && hasDraftState(row) ? DRAFT_ROW_BACKGROUND : undefined}>
        <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
          <Group gap={8} wrap="nowrap">
            <MobileCodeBadge>
              {t('common.codeBadge', { code: row.itemCode ?? '—' })}
            </MobileCodeBadge>
            {row.isNew && <NewItemBadge />}
          </Group>
          {!disabled && action}
        </Group>
        {description}
        {contractLine && (
          <Stack gap={4}>
            <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
              {t('common.recorded')} {recordedQty} / {contractLine.quantity ?? '—'} {row.unit ?? ''}
              {plannedQty != null &&
                ` · ${t('common.planned')} ${plannedQty} ${row.unit ?? ''} (${formatAmount(plannedQty * (contractLine.unitPrice ?? 0))})`}
            </Text>
            <Progress
              size={4}
              radius="xl"
              value={
                contractLine.quantity
                  ? Math.min(100, (recordedQty / contractLine.quantity) * 100)
                  : 0
              }
              color={isOver ? 'var(--color-status-danger)' : 'var(--color-accent-primary)'}
              styles={{ root: { backgroundColor: 'var(--color-bg-surface-2)' } }}
            />
            <Text
              fz={12.5}
              fw={600}
              style={{ color: isOver ? 'var(--color-status-danger)' : 'var(--color-text-primary)' }}
            >
              {t('common.remaining')} {remainingQty ?? '—'} {row.unit ?? ''} ·{' '}
              {formatAmount(remainingValue ?? 0)}
            </Text>
          </Stack>
        )}
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={2} pt={disabled ? 0 : 6}>
            <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
              {t('activityDetail.cardRealizedQty', { unit: row.unit ?? '' })}
            </Text>
            {contractLine && (
              <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                {formatAmount(achievedAmount)}
              </Text>
            )}
          </Stack>
          {qtyField}
        </Group>
      </MobileCard>
    );
  }

  return (
    <div
      style={{
        ...tableRowStyle,
        padding: '12px 20px',
        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
        backgroundColor: !disabled && hasDraftState(row) ? DRAFT_ROW_BACKGROUND : undefined
      }}
    >
      <Text fz={12} style={{ color: 'var(--color-text-muted)', width: 32 }}>
        {index + 1}
      </Text>
      <Text fz={12} style={{ color: mutedText ?? 'var(--color-text-primary)', width: 70 }}>
        {row.itemCode ?? '—'}
      </Text>
      <Group gap={8} wrap="nowrap" justify="space-between" style={{ flex: 1, minWidth: 0 }}>
        {description}
        {row.isNew && <NewItemBadge />}
      </Group>
      <Text fz={12} ta="center" style={{ color: 'var(--color-text-secondary)', width: 70 }}>
        {row.unit ?? '—'}
      </Text>
      <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
        <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
          {contractLine?.quantity ?? '—'}
        </Text>
        {contractLine && (
          <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
            {formatAmount(contractLine.totalPrice ?? 0)}
          </Text>
        )}
      </Stack>
      <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
        <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
          {contractLine ? recordedQty : '—'}
        </Text>
        {contractLine && (
          <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
            {formatAmount(recordedValue)}
          </Text>
        )}
      </Stack>
      <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
        <Text
          fz={13.5}
          fw={600}
          ta="right"
          style={{
            color:
              remainingQty != null && remainingQty < 0
                ? 'var(--color-status-danger)'
                : 'var(--color-text-primary)'
          }}
        >
          {remainingQty ?? '—'}
        </Text>
        {contractLine && (
          <Text
            fz={11}
            ta="right"
            style={{
              color:
                remainingValue != null && remainingValue < 0
                  ? 'var(--color-status-danger)'
                  : 'var(--color-text-muted)'
            }}
          >
            {formatAmount(remainingValue ?? 0)}
          </Text>
        )}
      </Stack>
      <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
        <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
          {plannedQty ?? '—'}
        </Text>
        {plannedQty != null && contractLine && (
          <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
            {formatAmount(plannedQty * (contractLine.unitPrice ?? 0))}
          </Text>
        )}
      </Stack>
      <Stack gap={2} style={{ width: 130, alignItems: 'flex-end' }}>
        {qtyField}
        {contractLine && (
          <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
            {formatAmount(achievedAmount)}
          </Text>
        )}
      </Stack>
      {!disabled && (
        <Group w={rowActionWidth} justify="flex-end">
          {action}
        </Group>
      )}
    </div>
  );
};

interface ActivityHourRowProps {
  row: DisplayHour;
  index: number;
  workerName: string;
  disabled: boolean;
  onChange: (value: DraftValue) => void;
  onRemove: () => void;
  variant?: 'row' | 'card';
}

const ActivityHourRow = ({
  row,
  index,
  workerName,
  disabled,
  onChange,
  onRemove,
  variant = 'row'
}: ActivityHourRowProps) => {
  const { t } = useTranslation();

  const hoursField = disabled ? (
    <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
      {row.savedHours}
    </Text>
  ) : (
    <DraftNumberInput
      width={90}
      height={variant === 'card' ? 40 : 32}
      allowDecimal
      decimalScale={2}
      value={row.hours}
      savedValue={row.savedHours}
      isNew={row.isNew}
      error={row.error}
      disabled={row.isRemoved}
      ariaLabel={t('activityDetail.colHours')}
      onChange={onChange}
    />
  );

  const action = row.isRemoved ? (
    <UndoRemoveButton onClick={onRemove} />
  ) : (
    <RemoveRowButton onClick={onRemove} />
  );

  const name = (
    <Text
      fz={13.5}
      fw={variant === 'card' ? 600 : undefined}
      truncate
      style={{
        color: row.isRemoved ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
        textDecoration: row.isRemoved ? 'line-through' : undefined,
        minWidth: 0
      }}
    >
      {workerName}
    </Text>
  );

  if (variant === 'card') {
    return (
      <MobileCard background={!disabled && hasDraftState(row) ? DRAFT_ROW_BACKGROUND : undefined}>
        <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text fz={12} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
              {index + 1}.
            </Text>
            {name}
            {row.isNew && <NewItemBadge />}
          </Group>
          {!disabled && action}
        </Group>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Text fz={13} pt={disabled ? 0 : 10} style={{ color: 'var(--color-text-secondary)' }}>
            {t('activityDetail.cardHours')}
          </Text>
          {hoursField}
        </Group>
      </MobileCard>
    );
  }

  return (
    <div
      style={{
        ...tableRowStyle,
        padding: '12px 20px',
        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
        backgroundColor: !disabled && hasDraftState(row) ? DRAFT_ROW_BACKGROUND : undefined
      }}
    >
      <Text fz={12} style={{ color: 'var(--color-text-muted)', width: 32 }}>
        {index + 1}
      </Text>
      <Group gap={8} wrap="nowrap" justify="space-between" style={{ flex: 1, minWidth: 0 }}>
        {name}
        {row.isNew && <NewItemBadge />}
      </Group>
      <div style={{ width: 130, display: 'flex', justifyContent: 'flex-end' }}>{hoursField}</div>
      {!disabled && (
        <Group w={rowActionWidth} justify="flex-end">
          {action}
        </Group>
      )}
    </div>
  );
};

export const ActivityDetailPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const activityId = id ?? '';

  const [itemPickerOpened, setItemPickerOpened] = useState(false);
  const [workerPickerOpened, setWorkerPickerOpened] = useState(false);
  const [assigneePickerOpened, setAssigneePickerOpened] = useState(false);
  const [removingAssignee, setRemovingAssignee] = useState<ActivityAssignee | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'lines' | 'hours'>('lines');
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  // Ista svrha kao na WorkPackageDetailPage — ako je brisanje odbijeno (aktivnost je u
  // međuvremenu uključena u tjedni izvještaj), to je trajnija informacija o stanju, ne
  // prolazna obavijest, pa ide kao banner na vrhu umjesto tosta.
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading } = useGetActivity(activityId, { query: { enabled: !!activityId } });
  const activity = data as Activity | undefined;
  const isLocked = !!activity?.weekReportId;

  const deleteMutation = useDeleteActivity({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListActivitiesQueryKey() });
        navigate('/activities', {
          state: { savedMessage: t('activityDetail.deleteSuccess') }
        });
      },
      onError: (error) => {
        setDeleteModalOpened(false);
        setActionError(activityErrorMessage(error, t('common.error')));
      }
    }
  });

  const { data: contractData } = useGetContract(activity?.contractId ?? '', {
    query: { enabled: !!activity?.contractId }
  });
  const contract = contractData as Contract | undefined;
  // FRAMEWORK ugovori smiju premašiti ugovorenu količinu (vidi ActivityService.assertWithinContractedQty).
  const isFrameworkContract = contract?.contractType === ContractContractType.FRAMEWORK;

  const { data: workPackageData } = useGetWorkPackage(activity?.workPackageId ?? '', {
    query: { enabled: !!activity?.workPackageId }
  });
  const workPackage = workPackageData as WorkPackage | undefined;

  const { data: spaceData } = useGetSpace(activity?.spaceId ?? '', {
    query: { enabled: !!activity?.spaceId }
  });
  const space = spaceData as Space | undefined;

  const { data: objectSpacesData } = useListSpaces(space?.objectId ?? '', {
    query: { enabled: !!space?.objectId }
  });
  const objectSpaces: Space[] = objectSpacesData ?? [];
  const spaceBreadcrumb = buildSpaceBreadcrumb(activity?.spaceId, objectSpaces);

  const { data: assigneesData } = useListActivityAssignees(activityId, {
    query: { enabled: !!activityId }
  });
  const assignees: ActivityAssignee[] = assigneesData ?? [];

  const { data: usersData } = useListUsers();
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    (usersData ?? []).forEach((u) => {
      if (u.id) map.set(u.id, `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim());
    });
    return map;
  }, [usersData]);

  const { data: linesData } = useListActivityLines(activityId, {
    query: { enabled: !!activityId }
  });
  const lines: ActivityLine[] = linesData ?? [];

  const { data: contractLinesData } = useListContractLines(activity?.contractId ?? '', {
    query: { enabled: !!activity?.contractId }
  });
  const contractLineById = useMemo(
    () => new Map((contractLinesData ?? []).map((cl) => [cl.id, cl])),
    [contractLinesData]
  );

  const { data: plannedItemsData } = useListPlannedItems(activity?.workPackageId ?? '', {
    query: { enabled: !!activity?.workPackageId }
  });
  const plannedItemById = useMemo(
    () => new Map((plannedItemsData ?? []).map((pi) => [pi.id, pi])),
    [plannedItemsData]
  );

  const { data: hoursData } = useListActivityHours(activityId, {
    query: { enabled: !!activityId }
  });
  const hours: ActivityHour[] = hoursData ?? [];

  // Stavke i sati se ne spremaju sami — izmjene, novi i uklonjeni redovi čekaju u nacrtu dok
  // korisnik ne klikne "Spremi stavke" / "Spremi sate" (jedan zahtjev, jedna transakcija).
  const linesDraft = useItemsDraft<ActivityPickedItem>();
  const hoursDraft = useItemsDraft<{ userId: string }>();
  const [justSavedTab, setJustSavedTab] = useState<'lines' | 'hours' | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const nonNegativeError = t('itemsDraft.errorNonNegative');

  const contractLineIdOf = (plannedItemId?: string, contractLineId?: string) =>
    contractLineId ??
    (plannedItemId ? plannedItemById.get(plannedItemId)?.contractLineId : undefined);

  // Strop retka (samo FIXED ugovor): koliko je još preostalo na cijelom ugovoru, uz vraćanje
  // vlastite spremljene količine. recordedQty je živi zbroj SVIH redaka aktivnosti na ovoj liniji
  // ugovora (uključujući ovaj) — isti izvor kao backend (ActivityService.assertWithinContractedQty).
  const qtyError = (value: DraftValue, contractLineId: string | undefined, savedQty: number) => {
    if (value === '') return nonNegativeError;
    const contractLine = contractLineId ? contractLineById.get(contractLineId) : undefined;
    if (isFrameworkContract || !contractLine) return null;
    const limit = Math.max(
      0,
      Math.trunc((contractLine.quantity ?? 0) - (contractLine.recordedQty ?? 0) + savedQty)
    );
    if (value <= limit) return null;
    return limit === 0
      ? t('activityDetail.qtyExhausted')
      : t('activityDetail.qtyMax', { max: limit });
  };

  const displayLines: DisplayLine[] = [
    ...lines.map((line): DisplayLine => {
      const lineId = line.id ?? '';
      const savedQty = line.realizedQty ?? 0;
      const realizedQty = linesDraft.valueOf(lineId, 'realizedQty', savedQty);
      const isRemoved = linesDraft.isRemoved(lineId);
      const isChanged = !isRemoved && realizedQty !== savedQty;
      const contractLineId = contractLineIdOf(line.plannedItemId, line.contractLineId);
      return {
        key: lineId,
        lineId,
        plannedItemId: line.plannedItemId,
        contractLineId,
        itemCode: line.itemCode,
        description: line.description,
        unit: line.unit,
        isNew: false,
        isRemoved,
        realizedQty,
        savedQty,
        isChanged,
        // Neizmijenjen spremljeni redak se ne označava — greška samo na onome što se sprema.
        error: isChanged ? qtyError(realizedQty, contractLineId, savedQty) : null
      };
    }),
    ...linesDraft.added.map((added): DisplayLine => {
      const realizedQty = added.values.realizedQty ?? 0;
      const contractLineId = contractLineIdOf(added.meta.plannedItemId, added.meta.contractLineId);
      return {
        key: added.tempId,
        tempId: added.tempId,
        plannedItemId: added.meta.plannedItemId,
        contractLineId,
        itemCode: added.meta.itemCode,
        description: added.meta.description,
        unit: added.meta.unit,
        isNew: true,
        isRemoved: false,
        realizedQty,
        savedQty: 0,
        isChanged: false,
        error: qtyError(realizedQty, contractLineId, 0)
      };
    })
  ];

  const displayHours: DisplayHour[] = [
    ...hours
      .filter((hour) => hour.userId)
      .map((hour): DisplayHour => {
        const hourId = hour.id ?? '';
        const savedHours = hour.hours ?? 0;
        const value = hoursDraft.valueOf(hourId, 'hours', savedHours);
        const isRemoved = hoursDraft.isRemoved(hourId);
        return {
          key: hourId,
          hourId,
          userId: hour.userId as string,
          isNew: false,
          isRemoved,
          hours: value,
          savedHours,
          isChanged: !isRemoved && value !== savedHours,
          error: !isRemoved && value === '' ? nonNegativeError : null
        };
      }),
    ...hoursDraft.added.map((added): DisplayHour => ({
      key: added.tempId,
      tempId: added.tempId,
      userId: added.meta.userId,
      isNew: true,
      isRemoved: false,
      hours: added.values.hours ?? 0,
      savedHours: 0,
      isChanged: false,
      error: added.values.hours === '' ? nonNegativeError : null
    }))
  ];

  const summarize = (
    rows: { isChanged: boolean; isRemoved: boolean; error: string | null }[],
    addedCount: number
  ): DraftSummary => {
    const changed = rows.filter((row) => row.isChanged).length;
    const removed = rows.filter((row) => row.isRemoved).length;
    return {
      changed,
      added: addedCount,
      removed,
      errors: rows.filter((row) => row.error).length,
      total: changed + addedCount + removed
    };
  };
  const linesSummary = summarize(displayLines, linesDraft.added.length);
  const hoursSummary = summarize(displayHours, hoursDraft.added.length);
  const combinedSummary: DraftSummary = {
    changed: linesSummary.changed + hoursSummary.changed,
    added: linesSummary.added + hoursSummary.added,
    removed: linesSummary.removed + hoursSummary.removed,
    errors: linesSummary.errors + hoursSummary.errors,
    total: linesSummary.total + hoursSummary.total
  };

  const setLineQty = (row: DisplayLine, value: DraftValue) => {
    setJustSavedTab(null);
    if (row.tempId) linesDraft.setAddedValue(row.tempId, 'realizedQty', value);
    else if (row.lineId) linesDraft.setValue(row.lineId, 'realizedQty', value);
  };
  const removeLine = (row: DisplayLine) => {
    setJustSavedTab(null);
    if (row.tempId) linesDraft.discardAdded(row.tempId);
    else if (row.lineId) linesDraft.toggleRemoved(row.lineId);
  };
  const setHourValue = (row: DisplayHour, value: DraftValue) => {
    setJustSavedTab(null);
    if (row.tempId) hoursDraft.setAddedValue(row.tempId, 'hours', value);
    else if (row.hourId) hoursDraft.setValue(row.hourId, 'hours', value);
  };
  const removeHour = (row: DisplayHour) => {
    setJustSavedTab(null);
    if (row.tempId) hoursDraft.discardAdded(row.tempId);
    else if (row.hourId) hoursDraft.toggleRemoved(row.hourId);
  };
  const addItems = (items: ActivityPickedItem[]) => {
    setJustSavedTab(null);
    linesDraft.addItems(items.map((item) => ({ meta: item, values: { realizedQty: 0 } })));
  };
  const addWorkers = (userIds: string[]) => {
    setJustSavedTab(null);
    hoursDraft.addItems(userIds.map((userId) => ({ meta: { userId }, values: { hours: 0 } })));
  };

  const existingPlannedItemIds = displayLines
    .map((row) => row.plannedItemId)
    .filter((itemId): itemId is string => !!itemId);
  const existingContractLineIds = displayLines
    .filter((row) => !row.plannedItemId)
    .map((row) => row.contractLineId)
    .filter((lineId): lineId is string => !!lineId);
  const existingHourUserIds = displayHours.map((row) => row.userId);

  const {
    page: linesPage,
    setPage: setLinesPage,
    pageSize: linesPageSize,
    setPageSize: setLinesPageSize,
    totalPages: linesTotalPages,
    pageItems: pageLines,
    offset: linesOffset
  } = useLocalPagination(displayLines);

  const {
    page: hoursPage,
    setPage: setHoursPage,
    pageSize: hoursPageSize,
    setPageSize: setHoursPageSize,
    totalPages: hoursTotalPages,
    pageItems: pageHourRows,
    offset: hoursOffset
  } = useLocalPagination(displayHours);

  // Traka UKUPNO ispod realizacije stavki — samo iznosi (ugovoreno / evidentirano / ostatak), zbroj
  // TRENUTNE stranice. Ti stupci su vrijednosti cijele stavke ugovora, pa se svaka
  // stavka ugovora broji jednom i kad je na nju vezano više redaka aktivnosti. Ostvareno prati i
  // nespremljene promjene (uklonjeni redak se ne broji).
  const lineTotals = useMemo(() => {
    const seen = new Set<string>();
    let contracted = 0;
    let recorded = 0;
    let achieved = 0;
    let planned: number | null = null;
    const seenPlanned = new Set<string>();
    pageLines.forEach((row) => {
      const contractLine = row.contractLineId
        ? contractLineById.get(row.contractLineId)
        : undefined;
      // Ostvareni iznos pripada SVAKOM retku aktivnosti (= Iznos na listi aktivnosti).
      if (!row.isRemoved) {
        achieved += (Number(row.realizedQty) || 0) * (contractLine?.unitPrice ?? 0);
      }
      // Planirani iznos samo za retke iz radnog paketa; svaka planirana stavka jednom.
      const plannedItem = row.plannedItemId ? plannedItemById.get(row.plannedItemId) : undefined;
      if (plannedItem?.id && plannedItem.plannedQty != null && !seenPlanned.has(plannedItem.id)) {
        seenPlanned.add(plannedItem.id);
        planned = (planned ?? 0) + plannedItem.plannedQty * (contractLine?.unitPrice ?? 0);
      }
      if (!row.contractLineId || !contractLine || seen.has(row.contractLineId)) return;
      seen.add(row.contractLineId);
      contracted += contractLine.totalPrice ?? 0;
      recorded += (contractLine.recordedQty ?? 0) * (contractLine.unitPrice ?? 0);
    });
    return { contracted, recorded, remaining: contracted - recorded, achieved, planned };
  }, [pageLines, plannedItemById, contractLineById]);

  // Ukupni ostvareni iznos CIJELE aktivnosti (spremljene stavke) — isti broj kao Iznos na listi
  // aktivnosti (Σ ostvarena količina × jedinična cijena stavke ugovora).
  const totalAchievedAmount = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const contractLineId =
          line.contractLineId ??
          (line.plannedItemId
            ? plannedItemById.get(line.plannedItemId)?.contractLineId
            : undefined);
        const unitPrice = contractLineId
          ? contractLineById.get(contractLineId)?.unitPrice
          : undefined;
        return sum + (line.realizedQty ?? 0) * (unitPrice ?? 0);
      }, 0),
    [lines, plannedItemById, contractLineById]
  );

  // Traka UKUPNO ispod sati po radniku — zbroj TRENUTNE stranice, uz nespremljene promjene.
  const totalHours = pageHourRows.reduce(
    (sum, row) => sum + (row.isRemoved ? 0 : Number(row.hours) || 0),
    0
  );

  const saveLinesMutation = useSaveActivityLines({
    mutation: { meta: { skipGlobalErrorToast: true } }
  });
  const saveHoursMutation = useSaveActivityHours({
    mutation: { meta: { skipGlobalErrorToast: true } }
  });

  const markSaved = (tab: 'lines' | 'hours') => {
    setJustSavedTab(tab);
    setSavedAt(
      new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
    );
  };

  const saveLinesDraft = async (): Promise<boolean> => {
    if (!activityId || linesSummary.errors > 0) return false;
    setSaveError(null);
    try {
      await saveLinesMutation.mutateAsync({
        id: activityId,
        data: {
          removedLineIds: displayLines
            .filter((row) => row.isRemoved && row.lineId)
            .map((row) => row.lineId as string),
          updatedLines: displayLines
            .filter((row) => row.isChanged && row.lineId)
            .map((row) => ({ lineId: row.lineId as string, realizedQty: Number(row.realizedQty) })),
          addedLines: linesDraft.added.map((added) => ({
            plannedItemId: added.meta.plannedItemId,
            contractLineId: added.meta.contractLineId,
            realizedQty: Number(added.values.realizedQty)
          }))
        }
      });
      // Linije ugovora nose recordedQty (zbroj svih aktivnosti) — bez osvježavanja bi strop ostao
      // na starom zbroju. Nacrt se briše tek kad su svježi podaci stigli.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListActivityLinesQueryKey(activityId) }),
        queryClient.invalidateQueries({ queryKey: getGetActivityQueryKey(activityId) }),
        activity?.contractId
          ? queryClient.invalidateQueries({
              queryKey: getListContractLinesQueryKey(activity.contractId)
            })
          : Promise.resolve()
      ]);
      linesDraft.reset();
      markSaved('lines');
      return true;
    } catch (error) {
      setSaveError(
        t('itemsDraft.saveFailed', { reason: activityErrorMessage(error, t('common.error')) })
      );
      return false;
    }
  };

  const saveHoursDraft = async (): Promise<boolean> => {
    if (!activityId || hoursSummary.errors > 0) return false;
    setSaveError(null);
    try {
      await saveHoursMutation.mutateAsync({
        id: activityId,
        data: {
          removedHourIds: displayHours
            .filter((row) => row.isRemoved && row.hourId)
            .map((row) => row.hourId as string),
          updatedHours: displayHours
            .filter((row) => row.isChanged && row.hourId)
            .map((row) => ({ hourId: row.hourId as string, hours: Number(row.hours) })),
          addedHours: hoursDraft.added.map((added) => ({
            userId: added.meta.userId,
            hours: Number(added.values.hours)
          }))
        }
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getListActivityHoursQueryKey(activityId) }),
        queryClient.invalidateQueries({ queryKey: getGetActivityQueryKey(activityId) })
      ]);
      hoursDraft.reset();
      markSaved('hours');
      return true;
    } catch (error) {
      setSaveError(
        t('itemsDraft.saveFailed', { reason: activityErrorMessage(error, t('common.error')) })
      );
      return false;
    }
  };

  const discardLinesDraft = () => {
    linesDraft.reset();
    setSaveError(null);
  };
  const discardHoursDraft = () => {
    hoursDraft.reset();
    setSaveError(null);
  };

  // Stavke i sati imaju svaki svoj nacrt i gumb; pri izlasku i akcijama jedno zajedničko pitanje.
  const { guard, modal: unsavedChangesModal } = useUnsavedChangesGuard({
    summary: combinedSummary,
    onSave: async () => {
      const linesSaved = linesSummary.total > 0 ? await saveLinesDraft() : true;
      const hoursSaved = hoursSummary.total > 0 ? await saveHoursDraft() : true;
      return linesSaved && hoursSaved;
    },
    onDiscard: () => {
      discardLinesDraft();
      discardHoursDraft();
    }
  });

  const removeAssigneeMutation = useRemoveActivityAssignee({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListActivityAssigneesQueryKey(activityId) });
        setRemovingAssignee(undefined);
      }
    }
  });

  if (isLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!activity) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('activityDetail.notFound')}
        </Text>
      </Container>
    );
  }

  const status = activityLockState(activity.weekReportId);
  const trackerSteps = buildActivityTrackerSteps(activity.weekReportId);

  const infoFields: InfoField[] = [
    {
      label: t('activityDetail.contract'),
      value: contract?.contractNumber ?? '—',
      ...(activity.contractId
        ? { onClick: () => navigate(`/contracts/${activity.contractId}`) }
        : {})
    },
    {
      label: t('activityDetail.workPackage'),
      value: workPackage?.name ?? t('activities.directFromContract'),
      ...(workPackage?.id ? { onClick: () => navigate(`/work-packages/${workPackage.id}`) } : {})
    },
    { label: t('activityDetail.space'), value: spaceBreadcrumb },
    {
      label: t('activityDetail.fieldAssignees'),
      value:
        assignees.length > 0
          ? t('activityDetail.assigneeCount', { count: assignees.length })
          : t('activityDetail.noAssignees')
    },
    { label: t('activityDetail.totalAchievedAmount'), value: formatAmount(totalAchievedAmount) }
  ];

  const editButton = !isLocked && (
    <Button
      variant="outline"
      onClick={() => guard(() => navigate(`/activities/${activityId}/edit`))}
    >
      {t('activityFormEdit.title')}
    </Button>
  );

  const deleteButton = !isLocked && (
    <Button color="red" variant="outline" onClick={() => guard(() => setDeleteModalOpened(true))}>
      {t('activityDetail.deleteAction')}
    </Button>
  );

  const renderLine = (row: DisplayLine, index: number, variant: 'row' | 'card') => {
    const plannedItem = row.plannedItemId ? plannedItemById.get(row.plannedItemId) : undefined;
    return (
      <ActivityLineRow
        key={row.key}
        variant={variant}
        row={row}
        index={linesOffset + index}
        disabled={isLocked}
        contractLine={row.contractLineId ? contractLineById.get(row.contractLineId) : undefined}
        plannedQty={plannedItem?.plannedQty}
        onChange={(value) => setLineQty(row, value)}
        onRemove={() => removeLine(row)}
      />
    );
  };

  const renderHour = (row: DisplayHour, index: number, variant: 'row' | 'card') => (
    <ActivityHourRow
      key={row.key}
      variant={variant}
      row={row}
      index={hoursOffset + index}
      workerName={userMap.get(row.userId) ?? '—'}
      disabled={isLocked}
      onChange={(value) => setHourValue(row, value)}
      onRemove={() => removeHour(row)}
    />
  );

  const tabs: CardTab[] = [
    {
      key: 'lines',
      label: t('activities.linesTitle'),
      caption: t('activityDetail.linesCaption'),
      captionInfo: (
        <PanelInfoIcon
          width={360}
          label={
            <InfoBulletList
              intro={t('columnsInfo.intro')}
              items={[
                t('columnsInfo.contracted'),
                t('columnsInfo.recorded'),
                t('columnsInfo.remaining'),
                t('columnsInfo.plannedQty'),
                t('columnsInfo.achievedQty'),
                t('columnsInfo.amounts')
              ]}
            />
          }
        />
      ),
      headerRight: !isLocked && (
        <Group gap={16} wrap="nowrap">
          <ItemsSaveStatus
            summary={linesSummary}
            justSaved={justSavedTab === 'lines'}
            savedAt={savedAt}
          />
          <Button size="sm" variant="outline" onClick={() => setItemPickerOpened(true)}>
            {t('activityDetail.addLineButton')}
          </Button>
        </Group>
      ),
      content: (
        <>
          <Box visibleFrom="sm">
            {displayLines.length > 0 && (
              <div
                className="table-header-row"
                style={{
                  ...tableRowStyle,
                  padding: '10px 20px',
                  backgroundColor: 'var(--color-bg-canvas)',
                  borderBottom: '1px solid var(--color-border-soft)'
                }}
              >
                <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', width: 32 }}>
                  {t('activityDetail.colIndex')}
                </Text>
                <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', width: 70 }}>
                  {t('activityDetail.colCode')}
                </Text>
                <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', flex: 1 }}>
                  {t('activityDetail.colDescription')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  ta="center"
                  style={{ color: 'var(--color-text-muted)', width: 70 }}
                >
                  {t('activityDetail.colUnit')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', width: 100 }}
                >
                  {t('activityDetail.colContracted')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', width: 100 }}
                >
                  {t('activityDetail.colRecorded')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', width: 100 }}
                >
                  {t('activityDetail.colRemaining')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', width: 100 }}
                >
                  {t('activityDetail.colPlannedQty')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', width: 130 }}
                >
                  {t('activityDetail.colRealizedQty')}
                </Text>
                {!isLocked && (
                  <Text
                    fz={10}
                    fw={600}
                    ta="right"
                    style={{ color: 'var(--color-text-muted)', width: rowActionWidth }}
                  >
                    {t('activityDetail.colRemove')}
                  </Text>
                )}
              </div>
            )}
            {pageLines.map((row, index) => renderLine(row, index, 'row'))}
            {displayLines.length > 0 && (
              <div
                style={{
                  ...tableRowStyle,
                  padding: '14px 20px',
                  borderTop: '2px solid var(--color-border-default)',
                  backgroundColor: 'var(--color-bg-canvas)'
                }}
              >
                <Stack gap={0} style={{ flex: 1 }}>
                  <Text fz={13} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                    {t('activityDetail.totalHours')}
                  </Text>
                  {linesSummary.total > 0 && (
                    <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                      {t('itemsDraft.includesUnsaved')}
                    </Text>
                  )}
                </Stack>
                <div style={{ width: 70 }} />
                <TotalAmountCell width={100} amount={lineTotals.contracted} />
                <TotalAmountCell width={100} amount={lineTotals.recorded} />
                <TotalAmountCell width={100} amount={lineTotals.remaining} />
                {lineTotals.planned != null ? (
                  <TotalAmountCell width={100} amount={lineTotals.planned} />
                ) : (
                  <div style={{ width: 100 }} />
                )}
                <TotalAmountCell width={130} amount={lineTotals.achieved} />
                {!isLocked && <div style={{ width: rowActionWidth }} />}
              </div>
            )}
            {!isLocked && (
              <ItemsSaveBar
                summary={linesSummary}
                isSaving={saveLinesMutation.isPending}
                onCancel={discardLinesDraft}
                onSave={saveLinesDraft}
              />
            )}
          </Box>
          {displayLines.length === 0 && (
            <Center py={24}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('activities.linesEmpty')}
              </Text>
            </Center>
          )}
          <MobileCardList>
            {pageLines.map((row, index) => renderLine(row, index, 'card'))}
            {displayLines.length > 0 && (
              <ListTotalCard
                amount={lineTotals.achieved}
                details={[
                  { label: t('activityDetail.colContracted'), amount: lineTotals.contracted },
                  { label: t('activityDetail.colRecorded'), amount: lineTotals.recorded },
                  ...(lineTotals.planned != null
                    ? [{ label: t('common.planned'), amount: lineTotals.planned }]
                    : []),
                  { label: t('activityDetail.colRemaining'), amount: lineTotals.remaining }
                ]}
              />
            )}
          </MobileCardList>
          {displayLines.length > 0 && (
            <ListPagination
              label={t('activities.linesShowing', {
                shown: pageLines.length,
                total: displayLines.length
              })}
              page={linesPage}
              totalPages={linesTotalPages}
              onPageChange={setLinesPage}
              pageSize={linesPageSize}
              onPageSizeChange={setLinesPageSize}
            />
          )}
        </>
      )
    },
    {
      key: 'hours',
      label: t('activities.hoursTitle'),
      caption: t('activityDetail.hoursCaption'),
      headerRight: !isLocked && (
        <Group gap={16} wrap="nowrap">
          <ItemsSaveStatus
            summary={hoursSummary}
            justSaved={justSavedTab === 'hours'}
            savedAt={savedAt}
          />
          <Button size="sm" variant="outline" onClick={() => setWorkerPickerOpened(true)}>
            {t('activityDetail.addWorkerButton')}
          </Button>
        </Group>
      ),
      content: (
        <>
          <Box visibleFrom="sm">
            {displayHours.length > 0 && (
              <div
                className="table-header-row"
                style={{
                  ...tableRowStyle,
                  padding: '10px 20px',
                  backgroundColor: 'var(--color-bg-canvas)',
                  borderBottom: '1px solid var(--color-border-soft)'
                }}
              >
                <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', width: 32 }}>
                  {t('activityDetail.colIndex')}
                </Text>
                <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', flex: 1 }}>
                  {t('activityDetail.colWorker')}
                </Text>
                <Text
                  fz={10}
                  fw={600}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)', width: 130 }}
                >
                  {t('activityDetail.colHours')}
                </Text>
                {!isLocked && (
                  <Text
                    fz={10}
                    fw={600}
                    ta="right"
                    style={{ color: 'var(--color-text-muted)', width: rowActionWidth }}
                  >
                    {t('activityDetail.colRemove')}
                  </Text>
                )}
              </div>
            )}
            {pageHourRows.map((row, index) => renderHour(row, index, 'row'))}
            {displayHours.length > 0 && (
              <div
                style={{
                  ...tableRowStyle,
                  padding: '14px 20px',
                  borderTop: '2px solid var(--color-border-default)',
                  backgroundColor: 'var(--color-bg-canvas)'
                }}
              >
                <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)', flex: 1 }}>
                  {t('activityDetail.totalHours')}
                </Text>
                <Text
                  fz={14}
                  fw={700}
                  ta="right"
                  style={{ color: 'var(--color-text-primary)', width: 130 }}
                >
                  {totalHours}
                </Text>
                {!isLocked && <div style={{ width: rowActionWidth }} />}
              </div>
            )}
            {!isLocked && (
              <ItemsSaveBar
                summary={hoursSummary}
                isSaving={saveHoursMutation.isPending}
                onCancel={discardHoursDraft}
                onSave={saveHoursDraft}
                saveLabel={t('itemsDraft.saveHours')}
              />
            )}
          </Box>
          {displayHours.length === 0 && (
            <Center py={24}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('activities.hoursEmpty')}
              </Text>
            </Center>
          )}
          <MobileCardList>
            {pageHourRows.map((row, index) => renderHour(row, index, 'card'))}
            {displayHours.length > 0 && (
              <Group
                justify="space-between"
                align="center"
                wrap="nowrap"
                p={12}
                style={{ borderRadius: 8, backgroundColor: 'var(--color-bg-surface-2)' }}
              >
                <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                  {t('activityDetail.totalHours')}
                </Text>
                <Text fz={15} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                  {totalHours}
                </Text>
              </Group>
            )}
          </MobileCardList>
          {displayHours.length > 0 && (
            <ListPagination
              label={t('activities.hoursShowing', {
                shown: pageHourRows.length,
                total: displayHours.length
              })}
              page={hoursPage}
              totalPages={hoursTotalPages}
              onPageChange={setHoursPage}
              pageSize={hoursPageSize}
              onPageSizeChange={setHoursPageSize}
            />
          )}
        </>
      )
    }
  ];

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/activities" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {actionError && (
          <StatusBanner
            tone="danger"
            message={actionError}
            onDismiss={() => setActionError(null)}
          />
        )}

        {saveError && (
          <StatusBanner tone="danger" message={saveError} onDismiss={() => setSaveError(null)} />
        )}

        <Group justify="space-between" wrap="wrap" gap={12} visibleFrom="sm">
          <Group gap={12}>
            <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
              {formatWeekLabel(activity.weekNumber, activity.reportYear)}
            </Title>
            <StatusPill label={t(status.labelKey)} bg={status.bg} fg={status.fg} header />
          </Group>
          <Group gap={12}>
            {editButton}
            {deleteButton}
          </Group>
        </Group>
        <Stack gap={8} hiddenFrom="sm">
          <Title order={2} fz={22} style={{ color: 'var(--color-text-primary)' }}>
            {formatWeekLabel(activity.weekNumber, activity.reportYear)}
          </Title>
          <StatusPill label={t(status.labelKey)} bg={status.bg} fg={status.fg} header />
          <MobileActionBar>
            {editButton}
            {deleteButton}
          </MobileActionBar>
        </Stack>

        <StatusTracker steps={trackerSteps} />

        {isLocked && (
          <CardSection title={t('activityDetail.lockedTitle')}>
            <Text p={20} fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('activityDetail.lockedMessage')}
            </Text>
          </CardSection>
        )}

        <InfoCard
          title={t('activityDetail.basicInfoTitle')}
          fields={infoFields}
          columns={4}
          borderColor="var(--color-border-default)"
        />

        <CardSection
          title={t('activityDetail.assigneesTitle')}
          headerRight={
            !isLocked && (
              <Text
                fz={13}
                fw={600}
                onClick={() => setAssigneePickerOpened(true)}
                style={{ cursor: 'pointer', color: 'var(--color-accent-primary-active)' }}
              >
                + {t('activityDetail.addAssignee')}
              </Text>
            )
          }
        >
          {assignees.length === 0 ? (
            <Stack align="center" gap={4} py={32}>
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                {t('activityDetail.assigneesEmptyTitle')}
              </Text>
              <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                {t('activityDetail.assigneesEmptyHint')}
              </Text>
            </Stack>
          ) : (
            <Stack gap={0}>
              {assignees.map((assignee, index) => (
                <div
                  key={assignee.id}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                  }}
                >
                  <Text fz={13.5} fw={600} style={{ flex: 1, color: 'var(--color-text-primary)' }}>
                    {assignee.teamId
                      ? assignee.teamName
                      : `${assignee.userFirstName ?? ''} ${assignee.userLastName ?? ''}`.trim()}
                  </Text>
                  <Text fz={12} w={120} style={{ color: 'var(--color-text-muted)' }}>
                    {assignee.teamId
                      ? t('activityDetail.assigneeTypeTeam')
                      : (assignee.userEmail ?? t('activityDetail.assigneeTypeIndividual'))}
                  </Text>
                  {!isLocked && (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => setRemovingAssignee(assignee)}
                      style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
                    >
                      <IconFigmaXCircle size={16} />
                    </Box>
                  )}
                </div>
              ))}
            </Stack>
          )}
        </CardSection>

        <CardTabs
          tabs={tabs}
          active={activeTab}
          onChange={(key) => setActiveTab(key as typeof activeTab)}
        />
      </Stack>

      {!isLocked && (
        <>
          <ActivityItemPickerModal
            opened={itemPickerOpened}
            onClose={() => setItemPickerOpened(false)}
            workPackageId={activity.workPackageId}
            contractId={activity.contractId ?? ''}
            existingPlannedItemIds={existingPlannedItemIds}
            existingContractLineIds={existingContractLineIds}
            onAdd={addItems}
          />
          <ActivityWorkerPickerModal
            opened={workerPickerOpened}
            onClose={() => setWorkerPickerOpened(false)}
            existingUserIds={existingHourUserIds}
            onAdd={addWorkers}
          />
          <ActivityAssigneePickerModal
            opened={assigneePickerOpened}
            onClose={() => setAssigneePickerOpened(false)}
            activityId={activityId}
            existingTeamIds={assignees
              .map((assignee) => assignee.teamId)
              .filter((value): value is string => !!value)}
            existingUserIds={assignees
              .map((assignee) => assignee.userId)
              .filter((value): value is string => !!value)}
          />
          <ConfirmActionModal
            opened={!!removingAssignee}
            onClose={() => setRemovingAssignee(undefined)}
            onConfirm={() =>
              removingAssignee?.id &&
              removeAssigneeMutation.mutate({ id: activityId, assigneeId: removingAssignee.id })
            }
            loading={removeAssigneeMutation.isPending}
            isPaused={removeAssigneeMutation.isPaused}
            danger
            title={t('activityDetail.removeAssignee')}
            message={
              removingAssignee?.teamId
                ? (removingAssignee?.teamName ?? '')
                : `${removingAssignee?.userFirstName ?? ''} ${removingAssignee?.userLastName ?? ''}`.trim()
            }
            confirmLabel={t('activityDetail.removeAssignee')}
          />
          <ConfirmActionModal
            opened={deleteModalOpened}
            onClose={() => setDeleteModalOpened(false)}
            onConfirm={() => deleteMutation.mutate({ id: activityId })}
            loading={deleteMutation.isPending}
            isPaused={deleteMutation.isPaused}
            danger
            title={t('activityDetail.deleteTitle')}
            message={t('activityDetail.deleteMessage', {
              week: formatWeekLabel(activity.weekNumber, activity.reportYear)
            })}
            confirmLabel={t('activityDetail.deleteConfirm')}
          />
        </>
      )}

      {!isLocked && (
        <Box hiddenFrom="sm">
          {activeTab === 'lines' ? (
            <ItemsSaveBar
              variant="mobile"
              summary={linesSummary}
              isSaving={saveLinesMutation.isPending}
              onCancel={discardLinesDraft}
              onSave={saveLinesDraft}
            />
          ) : (
            <ItemsSaveBar
              variant="mobile"
              summary={hoursSummary}
              isSaving={saveHoursMutation.isPending}
              onCancel={discardHoursDraft}
              onSave={saveHoursDraft}
              saveLabel={t('itemsDraft.saveHours')}
            />
          )}
        </Box>
      )}

      {unsavedChangesModal}
    </Container>
  );
};
